import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Allow local mock-only dev when Supabase isn't configured: every auth
// helper that touches `supabase` should null-check the export.
export const isSupabaseConfigured = Boolean(url && anonKey);

// ── H-3: sessionStorage adapter for the Supabase auth session ────
//
// Audit finding H-3 from Documentation/SECURITY_AUDIT_2026-05-13.md.
// Default supabase-js persists the session to localStorage which lives
// across tabs and browser restarts. A successful XSS — even one that
// CSP-enforce (PR #25) lets through — could exfiltrate that session.
//
// This adapter narrows the blast radius by keeping the session in
// sessionStorage instead:
//   • Closing the tab clears the session (and so signs the user out).
//   • Each new tab starts with no session and prompts a fresh sign-in.
//   • An XSS in the dashboard can still steal the live session, but
//     only for the open tab and only until that tab closes — vs.
//     persisting across the user's browser lifetime.
//
// The adapter implements the SimpleStorage interface supabase-js
// expects: getItem/setItem/removeItem (sync; nullable returns).
// It gracefully no-ops when window/sessionStorage isn't available
// (SSR, test runner without jsdom, security-hardened browser modes).
//
// Companion: the response interceptor in src/services/api.js + the
// boot-timeout in src/contexts/AuthContext.jsx (both PR #14) handle
// recovery when the session is missing — i.e. after a tab close the
// user lands on /signin?reason=session_expired and re-authenticates.

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

const sessionStorageAdapter = {
  getItem(key) {
    if (!isBrowser()) return null;
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key, value) {
    if (!isBrowser()) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Quota exceeded, private mode quirks, etc. — swallow so a single
      // bad write doesn't break the auth flow. Worst case the session
      // simply isn't persisted and the user re-signs in.
    }
  },
  removeItem(key) {
    if (!isBrowser()) return;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* see setItem */
    }
  },
};

// One-time migration: if a previous deploy persisted the session in
// localStorage, copy it across to sessionStorage on first load so the
// user isn't surprised by an immediate sign-out the day this ships.
// We then strip the localStorage copies entirely so the original
// threat (long-lived persistence) is closed.
function migrateLocalStorageSession() {
  if (!isBrowser() || typeof window.localStorage === 'undefined') return;
  try {
    const toMigrate = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && (k.startsWith('sb-') || k.startsWith('supabase.auth.'))) {
        toMigrate.push(k);
      }
    }
    for (const k of toMigrate) {
      const v = window.localStorage.getItem(k);
      if (v != null && window.sessionStorage.getItem(k) == null) {
        try { window.sessionStorage.setItem(k, v); } catch { /* ignore quota */ }
      }
      try { window.localStorage.removeItem(k); } catch { /* ignore */ }
    }
  } catch {
    // Don't let migration failures block boot.
  }
}
migrateLocalStorageSession();

export const supabase = isSupabaseConfigured
  ? createClient(url, anonKey, {
      auth: {
        // H-3: keep session out of localStorage. See adapter above.
        storage: sessionStorageAdapter,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
    })
  : null;

if (!isSupabaseConfigured && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn(
    '[supabase] REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY is missing. ' +
    'Auth gate is bypassed in dev mode and the dashboard runs against mock data.'
  );
}
